"use client";

// src/lib/calculator/use-calculator-state.ts
//
// Single live source of truth for the peptide calculator's input state. Owned by
// PeptideCalculatorClient so future surfaces (cockpit, sticky pill, history,
// buy-bridge) can all read ONE reducer instead of each re-deriving state.
//
// Persistence: prefs (NOT syringe size) are debounced to localStorage; on mount
// they restore unless the peptide is URL-locked (spoke). Syringe size normally
// resets to 1.0 ml on load — see calculator-storage.ts header. An explicit locale
// transition can carry the current live state once, without persisting the barrel.

import { useReducer, useEffect, useMemo, useRef, useCallback } from "react";
import type { Locale } from "@/config/locale";
import { getPeptideBySlug, getPeptideCategory, getPeptideName } from "./peptide-data";
import { calculateAll, defaultUnitOf } from "./calculations";
import { loadPrefs, savePrefs, pushHistory, historySig, type CalculatorPrefs } from "./calculator-storage";
import { consumeCalculatorLocaleTransition } from "./locale-transition";
import type { SyringeSize, DisplayUnit, DoseUnitMode, ReverseProfile, CalculationResult } from "./types";

const PREFS_DEBOUNCE_MS = 1100;
const HISTORY_DEBOUNCE_MS = 1100;
const DEFAULT_SLUG = "retatrutide";

// ─── State ─────────────────────────────────────────────────────────────────────

export interface CalculatorState {
	peptideSlug: string;
	peptideAmount: number;
	bacWater: number;
	/** Internal dose in mg. Display converts via doseUnitMode. */
	dose: number;
	/** 1.0 on ordinary loads; only a live locale transition can carry another size. */
	syringeSize: SyringeSize;
	calibration: 40 | 100;
	displayUnit: DisplayUnit;
	doseUnitMode: DoseUnitMode;
	mode: "forward" | "reverse";
	reverseProfile: ReverseProfile;
	/** True once the user has manually picked a display unit — survives peptide switches. */
	unitTouched: boolean;
}

function categoryUnitDefault(slug: string, locale: Locale): DisplayUnit {
	const peptide = getPeptideBySlug(slug);
	const category = peptide ? getPeptideCategory(peptide, locale) : "";
	return defaultUnitOf(category);
}

function defaultsForSlug(
	slug: string,
	locale: Locale,
): {
	peptideAmount: number;
	bacWater: number;
	dose: number;
	doseUnitMode: DoseUnitMode;
	displayUnit: DisplayUnit;
} {
	const peptide = getPeptideBySlug(slug);
	return {
		peptideAmount: peptide?.defaultVialSize ?? 5,
		bacWater: peptide?.defaultBacWater ?? 2,
		dose: peptide?.defaultDose ?? 0.25,
		doseUnitMode: peptide?.doseUnit ?? "mg",
		displayUnit: categoryUnitDefault(slug, locale),
	};
}

function makeInitialState(slug: string, locale: Locale): CalculatorState {
	const d = defaultsForSlug(slug, locale);
	return {
		peptideSlug: slug,
		peptideAmount: d.peptideAmount,
		bacWater: d.bacWater,
		dose: d.dose,
		syringeSize: 1.0,
		calibration: 100,
		displayUnit: d.displayUnit,
		doseUnitMode: d.doseUnitMode,
		mode: "forward",
		reverseProfile: "easier",
		unitTouched: false,
	};
}

// ─── Reducer ───────────────────────────────────────────────────────────────────

type Action =
	| { type: "SET_PEPTIDE"; slug: string; locale: Locale }
	| { type: "SET_AMOUNT"; value: number }
	| { type: "SET_BAC"; value: number }
	| { type: "SET_DOSE"; value: number }
	| { type: "SET_SYRINGE"; value: SyringeSize }
	| { type: "SET_CALIBRATION"; value: 40 | 100 }
	| { type: "SET_DISPLAY_UNIT"; value: DisplayUnit }
	| { type: "SET_DOSE_UNIT_MODE"; value: DoseUnitMode }
	| { type: "SET_MODE"; value: "forward" | "reverse" }
	| { type: "SET_REVERSE_PROFILE"; value: ReverseProfile }
	| { type: "RESET"; slug: string; locale: Locale }
	| { type: "RESTORE_LOCALE_TRANSITION"; state: CalculatorState }
	| { type: "RESTORE"; prefs: CalculatorPrefs; lockedPeptide: boolean; locale: Locale }
	| { type: "RESTORE_FROM_PARAMS"; params: ShareParams; lockedPeptide: boolean; locale: Locale };

/**
 * Decoded share-link query params. A shared calculator URL carries
 * `?p={slug}&vial={mg}&bac={ml}&dose={mg}&unit={displayUnit}`; this is the shape
 * after parsing. Every field is optional so a partial/old link still prefills
 * whatever it can.
 */
export interface ShareParams {
	peptideSlug?: string;
	peptideAmount?: number;
	bacWater?: number;
	dose?: number;
	displayUnit?: DisplayUnit;
}

function reducer(state: CalculatorState, action: Action): CalculatorState {
	switch (action.type) {
		case "SET_PEPTIDE": {
			const d = defaultsForSlug(action.slug, action.locale);
			return {
				...state,
				peptideSlug: action.slug,
				// Changing peptide resets vial/bac/dose/doseUnitMode to the new peptide's
				// defaults. Syringe stays 1.0 (never reset away from the safe barrel).
				peptideAmount: d.peptideAmount,
				bacWater: d.bacWater,
				dose: d.dose,
				doseUnitMode: d.doseUnitMode,
				// Only recompute the display unit from the new peptide's category when the
				// user hasn't manually pinned a unit — a pinned unit survives peptide switches.
				displayUnit: state.unitTouched ? state.displayUnit : d.displayUnit,
			};
		}
		case "SET_AMOUNT":
			return { ...state, peptideAmount: action.value };
		case "SET_BAC":
			return { ...state, bacWater: action.value };
		case "SET_DOSE":
			return { ...state, dose: action.value };
		case "SET_SYRINGE":
			return { ...state, syringeSize: action.value };
		case "SET_CALIBRATION":
			return { ...state, calibration: action.value };
		case "SET_DISPLAY_UNIT":
			// A manual unit pick sticks across peptide switches.
			return { ...state, displayUnit: action.value, unitTouched: true };
		case "SET_DOSE_UNIT_MODE":
			return { ...state, doseUnitMode: action.value };
		case "SET_MODE":
			return { ...state, mode: action.value };
		case "SET_REVERSE_PROFILE":
			return { ...state, reverseProfile: action.value };
		case "RESET":
			return makeInitialState(action.slug, action.locale);
		case "RESTORE_LOCALE_TRANSITION":
			return { ...action.state };
		case "RESTORE": {
			const { prefs, lockedPeptide, locale } = action;
			// On a spoke (lockedPeptide), the URL slug WINS over the stored peptide; we
			// keep the current peptide-derived fields and only adopt non-peptide prefs.
			if (lockedPeptide) {
				return {
					...state,
					calibration: prefs.calibration,
					displayUnit: prefs.displayUnit,
					// displayUnit came from a manual pick on a previous visit → treat as touched
					// so it survives a later peptide switch.
					unitTouched: true,
				};
			}
			// Hub: adopt the full stored peptide context. NEVER restore syringeSize.
			void locale; // peptide-derived fields come straight from prefs here
			return {
				...state,
				peptideSlug: prefs.peptideSlug,
				peptideAmount: prefs.peptideAmount,
				bacWater: prefs.bacWater,
				dose: prefs.dose,
				calibration: prefs.calibration,
				displayUnit: prefs.displayUnit,
				doseUnitMode: prefs.doseUnitMode,
				unitTouched: true,
			};
		}
		case "RESTORE_FROM_PARAMS": {
			const { params, lockedPeptide, locale } = action;
			// On the hub a shared link adopts the peptide too; on a spoke the URL peptide
			// stays locked (we keep state.peptideSlug) but vial/bac/dose still prefill.
			const targetSlug = !lockedPeptide && params.peptideSlug ? params.peptideSlug : state.peptideSlug;
			// Pull defaults for the (possibly new) peptide so doseUnitMode + any unset field
			// land coherently, then overlay whatever the link specified.
			const d = defaultsForSlug(targetSlug, locale);
			return {
				...state,
				peptideSlug: targetSlug,
				doseUnitMode: d.doseUnitMode,
				peptideAmount: params.peptideAmount ?? d.peptideAmount,
				bacWater: params.bacWater ?? d.bacWater,
				dose: params.dose ?? d.dose,
				displayUnit: params.displayUnit ?? state.displayUnit,
				unitTouched: params.displayUnit ? true : state.unitTouched,
			};
		}
		default:
			return state;
	}
}

const DISPLAY_UNITS: readonly DisplayUnit[] = ["iu", "ml", "marks"];

/** Parses a finite, positive number from a query-string value, else undefined. */
function parsePositiveNumber(raw: string | null): number | undefined {
	if (raw == null) return undefined;
	const n = Number(raw);
	return Number.isFinite(n) && n > 0 ? n : undefined;
}

/**
 * Reads share-link params off the current URL (SSR-safe). Returns null when there's
 * no window or no recognizable calculator params, so callers can skip the dispatch.
 */
function readShareParams(): ShareParams | null {
	if (typeof window === "undefined") return null;
	let sp: URLSearchParams;
	try {
		sp = new URL(window.location.href).searchParams;
	} catch {
		return null;
	}
	const peptideSlug = sp.get("p")?.trim() || undefined;
	const peptideAmount = parsePositiveNumber(sp.get("vial"));
	const bacWater = parsePositiveNumber(sp.get("bac"));
	const dose = parsePositiveNumber(sp.get("dose"));
	const unitRaw = sp.get("unit");
	const displayUnit =
		unitRaw && DISPLAY_UNITS.includes(unitRaw as DisplayUnit) ? (unitRaw as DisplayUnit) : undefined;
	// Only treat as a share link when at least one calculator field is present.
	if (!peptideSlug && peptideAmount == null && bacWater == null && dose == null && !displayUnit) {
		return null;
	}
	// Guard against an unknown peptide slug (e.g. a stale/forged link) — drop it so we
	// don't switch to a non-existent peptide. The other fields still prefill.
	const safeSlug = peptideSlug && getPeptideBySlug(peptideSlug) ? peptideSlug : undefined;
	return { peptideSlug: safeSlug, peptideAmount, bacWater, dose, displayUnit };
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

export interface UseCalculatorStateArgs {
	initialSlug?: string;
	lockedPeptide?: boolean;
	locale: string;
	channel: string;
}

export function useCalculatorState({
	initialSlug = DEFAULT_SLUG,
	lockedPeptide = false,
	locale,
	channel,
}: UseCalculatorStateArgs) {
	const loc = locale as Locale;
	const [state, dispatch] = useReducer(reducer, undefined, () => makeInitialState(initialSlug, loc));

	// ── Restore on mount or Activity reveal (client only) ──
	// An explicit locale transition carries the latest live inputs ahead of an old
	// share query. On ordinary loads a share link WINS over stored prefs so a shared URL
	// always opens the calculator exactly as it was shared. Absent a share link, we
	// fall back to the persisted prefs as before.
	const restoredRef = useRef(false);
	useEffect(() => {
		// A retained page has its own route props. Never let an old locale, channel,
		// hub or spoke consume the transition belonging to the currently visible page.
		const pathname = `/${locale}/${channel}/peptide-calculator${lockedPeptide ? `/${initialSlug}` : ""}`;
		if (window.location.pathname !== pathname) return;
		const transition = consumeCalculatorLocaleTransition(pathname);
		if (transition) {
			restoredRef.current = true;
			dispatch({ type: "RESTORE_LOCALE_TRANSITION", state: transition });
			return;
		}
		if (restoredRef.current) return;
		restoredRef.current = true;
		const shareParams = readShareParams();
		if (shareParams) {
			dispatch({ type: "RESTORE_FROM_PARAMS", params: shareParams, lockedPeptide, locale: loc });
			return;
		}
		const prefs = loadPrefs();
		if (prefs) {
			dispatch({ type: "RESTORE", prefs, lockedPeptide, locale: loc });
		}
		// Route props are fixed per tree; Activity reconnects this effect on reveal.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// ── Debounced prefs persistence ──
	const prefsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	useEffect(() => {
		// Don't write until the initial restore has run, so we never clobber stored
		// prefs with the seed defaults on first paint.
		if (!restoredRef.current) return;
		if (prefsTimer.current) clearTimeout(prefsTimer.current);
		prefsTimer.current = setTimeout(() => {
			savePrefs({
				peptideSlug: state.peptideSlug,
				peptideAmount: state.peptideAmount,
				bacWater: state.bacWater,
				dose: state.dose,
				calibration: state.calibration,
				displayUnit: state.displayUnit,
				doseUnitMode: state.doseUnitMode,
			});
		}, PREFS_DEBOUNCE_MS);
		return () => {
			if (prefsTimer.current) clearTimeout(prefsTimer.current);
		};
	}, [
		state.peptideSlug,
		state.peptideAmount,
		state.bacWater,
		state.dose,
		state.calibration,
		state.displayUnit,
		state.doseUnitMode,
	]);

	// ── Derived calculation ──
	const result: CalculationResult = useMemo(
		() =>
			calculateAll({
				peptideAmount: state.peptideAmount,
				bacWater: state.bacWater,
				dose: state.dose,
				syringeSize: state.syringeSize,
				calibration: state.calibration,
			}),
		[state.peptideAmount, state.bacWater, state.dose, state.syringeSize, state.calibration],
	);

	// ── Setters ──
	const setPeptideSlug = useCallback(
		(slug: string) => dispatch({ type: "SET_PEPTIDE", slug, locale: loc }),
		[loc],
	);
	const setPeptideAmount = useCallback((value: number) => dispatch({ type: "SET_AMOUNT", value }), []);
	const setBacWater = useCallback((value: number) => dispatch({ type: "SET_BAC", value }), []);
	const setDose = useCallback((value: number) => dispatch({ type: "SET_DOSE", value }), []);
	const setSyringeSize = useCallback((value: SyringeSize) => dispatch({ type: "SET_SYRINGE", value }), []);
	const setCalibration = useCallback((value: 40 | 100) => dispatch({ type: "SET_CALIBRATION", value }), []);
	const setDisplayUnit = useCallback(
		(value: DisplayUnit) => dispatch({ type: "SET_DISPLAY_UNIT", value }),
		[],
	);
	const setDoseUnitMode = useCallback(
		(value: DoseUnitMode) => dispatch({ type: "SET_DOSE_UNIT_MODE", value }),
		[],
	);
	const setMode = useCallback((value: "forward" | "reverse") => dispatch({ type: "SET_MODE", value }), []);
	const setReverseProfile = useCallback(
		(value: ReverseProfile) => dispatch({ type: "SET_REVERSE_PROFILE", value }),
		[],
	);
	const reset = useCallback(
		() => dispatch({ type: "RESET", slug: initialSlug, locale: loc }),
		[initialSlug, loc],
	);

	// ── History ──
	const pushToHistory = useCallback(() => {
		const peptide = getPeptideBySlug(state.peptideSlug);
		const peptideName = peptide ? getPeptideName(peptide, loc) : state.peptideSlug;
		const doseDisplay =
			state.doseUnitMode === "mcg" ? `${Math.round(state.dose * 1000)} mcg` : `${state.dose} mg`;
		pushHistory({
			sig: historySig(state.peptideSlug, state.peptideAmount, state.bacWater, state.dose),
			peptideSlug: state.peptideSlug,
			peptideName,
			peptideAmount: state.peptideAmount,
			bacWater: state.bacWater,
			dose: state.dose,
			doseDisplay,
			iu: Math.round(result.insulinUnits),
			mode: state.mode,
		});
	}, [state, result.insulinUnits, loc]);

	// ── Debounced history write ──
	// Mirrors the prefs cadence (~1.1s): once a calculation settles (real volume),
	// persist a deduped (by sig) history entry — capped at 6 by pushHistory. We never
	// write on every keystroke, and we skip until the initial restore has run so a
	// half-restored seed state can't land in the strip.
	const historyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	useEffect(() => {
		if (!restoredRef.current) return;
		if (!(result.volume > 0)) return;
		if (historyTimer.current) clearTimeout(historyTimer.current);
		historyTimer.current = setTimeout(() => {
			pushToHistory();
		}, HISTORY_DEBOUNCE_MS);
		return () => {
			if (historyTimer.current) clearTimeout(historyTimer.current);
		};
	}, [state.peptideSlug, state.peptideAmount, state.bacWater, state.dose, result.volume, pushToHistory]);

	return {
		state,
		setPeptideSlug,
		setPeptideAmount,
		setBacWater,
		setDose,
		setSyringeSize,
		setCalibration,
		setDisplayUnit,
		setDoseUnitMode,
		setMode,
		setReverseProfile,
		reset,
		result,
		pushToHistory,
	};
}
